import { Injector, NgZone } from '@angular/core'
import { Http } from '@angular/http'
import { FormBuilder } from '@angular/forms'
import * as moment from 'moment-timezone'
import * as inflection from 'inflection'
import { Cookie } from 'ng2-cookies/ng2-cookies'

import {
  HttpHelper,
  AppContext,
  Utils,
  ObjectHelper,
  AppInjector,
  ClassRegistry,
  FormHelper,
  DateHelper,
  AlphabetGenerator,
  DomUtils,
  RedirectHelper,
  TreeUtils,
} from './helpers'
import { TinyMceHelper } from './angular/components/form/tiny-mce/tiny-mce-helper'
import { TinyMceModule } from './angular/components/form/tiny-mce/tiny-mce-helper/tiny-mce.module'
import { FlowplayerService } from './angular/components/flowplayer/flowplayer.service'
import { FlowplayerClipFactory } from './angular/components/flowplayer/flowplayer-clip.factory'
import { BaseModel } from './data/models'
import { ApiErrorFactory, ApiTokenFactory, HttpStatus } from './core/models'
import { PromptFactory } from './forms/models/prompt/prompt-factory'
import {
  CreateByTypeStrategy,
  CreateDefaultStrategy,
  EntityAbstractFactory,
  ModelFactory,
  QuestionFactory,
  QuestionContextCreateByTypeStrategy,
  TestExecutionFactory
} from './data/factories'
import { UriFactory, TimerFactory } from './core/factories'
import {
  JsonApiProcessor,
  JsonApiOperationRequest
} from './data/services/operation/operation-processors'
import {
  AuthenticationService,
  EntityListRegistryService,
  IEntityListConfig,
  MathJaxService,
  USER_ENDPOINT_MAPPING
} from './services'
import {
  FormMapper,
  IFormMapping,
  PromptControlService,
  QuestionViewFormMapper
} from './forms/services'
import { AuthGuard } from './guards'
import {
  ModelRepository,
  ModelRepositoryAdapter,
  ModelMapper,
  ModelIdentityMap,
  JsonApiBackend,
  Store,
  JsonapiUrlGenerator,
  EntityMappingManager,
  IEntityMappingConfig,
  EntityMappingFactory,
  PresenterFactory,
  GuaranteedFindDecoratorFactory,
  YaysonPresenter,
  TEST_EXECUTION_MAPPING_CONFIG
} from './model-repository'

import {
  AnswerChoiceService,
  AnswerChoiceGroupService,
  CategoryService,
  UserAnswerService,
  ContainerService,
  ExplanationService,
  ExplanationTypeService,
  LabelService,
  QuestionContextService,
  QuestionContextTypeService,
  QuestionPromptService,
  QuestionService,
  QuestionTypeService,
  TestExecutionService,
  TopicService,
  UserService,
  TestSegmentService,
  ScoreResultService,
  TestExecutionResultService,
  UploadService,
  UPLOAD_ENDPOINT_MAPPING,
  USER_ANSWER_ENDPOINT_MAPPING,
  CourseService,
  CartItemAssociationService,
  ProductRecommendationService,
  KtpEnrollmentRecommendationService,
  GraphqlService
} from './data/services'

import { MessageService, AppStateService, ModuleStateService, AppMetricsService, TextQuestionAutosaver, ExamState, EnvironmentService, QuestionCorrectnessService } from './core/services'

import { QuestionViewService } from './angular'

import * as DataServices from './data/services'
import * as DataModels from './data/models'
import * as Presenters from './data/presenters'

import { HttpClient } from '@angular/common/http'

export class ProvidersBuilder {
    public constructor(
      private _apiRoot: string,
      private _endpointMapping: {},
      private _entityMappingConfigs: IEntityMappingConfig[],
      private _modelClasses: Array<{new(...args): BaseModel}>,
      private _formMappings: IFormMapping[],
      private _entityListConfigs: IEntityListConfig[],
      private _presenterClasses: Array<{new(): YaysonPresenter }>,
      private _appVersion: number,
      private _appInjectorClasses: Array<{new(): {}}>,
      private _logInUrl: string,
      private _logoutUrl: string,
      private _environmentConfigs: {},
      private _byTypeFactoryClasses: any[] = []
    ) {}

    public build(): any[] {
      const dataServiceProviders = this._createDataServiceProviders()
      const operationServiceProviders = this._createOperationServiceProviders()

      const dataModelClasses = Object.keys(DataModels)
        .filter((key) => DataModels[key].prototype instanceof BaseModel)
        .map((key) => DataModels[key])

      const dataServiceClasses = Object.keys(DataServices)
        .filter((key) => DataServices[key].prototype instanceof ModelRepositoryAdapter)
        .map((key) => DataServices[key])

      const presentersClasses = Object.keys(Presenters)
        .filter((key) => Presenters[key].prototype instanceof YaysonPresenter)
        .map((key) => Presenters[key]).concat(this._presenterClasses || [])

      const utils = new Utils(window, inflection)
      const objectHelper = new ObjectHelper()
      const treeUtils = new TreeUtils()
      const httpStatus = new HttpStatus()
      const apiErrorFactory = new ApiErrorFactory(utils, httpStatus)
      const modelFactory = new ModelFactory(this._modelClasses.concat(dataModelClasses), utils)
      const appContext = new AppContext(modelFactory)
      const apiTokenFactory = new ApiTokenFactory()
      const promptFactory = new PromptFactory()
      const tinyMceHelper = new TinyMceHelper(TinyMceModule)
      const messageService = new MessageService(utils)
      const appStateService = new AppStateService()
      const formHelper = new FormHelper(messageService)
      const formMapper = new FormMapper(this._formMappings, promptFactory, utils, formHelper)
      const appInjectorClasses = new ClassRegistry(utils, this._appInjectorClasses.concat(dataServiceClasses))
      const redirectHelper = new RedirectHelper(this._logInUrl, this._logoutUrl, window)
      const libEndpoints = [UPLOAD_ENDPOINT_MAPPING, USER_ENDPOINT_MAPPING, USER_ANSWER_ENDPOINT_MAPPING]
      const libEndpointMappings = libEndpoints.reduce((previous, endpoint) => {
        return Object.assign({}, previous, endpoint)
      }, {})
      const endpoints = Object.assign({}, libEndpointMappings, this._endpointMapping)
      const entityListRegistryService = new EntityListRegistryService(this._entityListConfigs, utils)
      const alphabetGenerator = new AlphabetGenerator()
      const questionViewFormMapper = new QuestionViewFormMapper(promptFactory)
      const uriFactory = new UriFactory()
      const timerFactory = new TimerFactory()

      this._byTypeFactoryClasses = this._byTypeFactoryClasses.concat([QuestionContextCreateByTypeStrategy])

      const appMetricsService = new AppMetricsService(Cookie)
      const httpHelperFactory = this._httpHelperFactory(apiErrorFactory, httpStatus, this._appVersion, appMetricsService)
      const promptControlServiceFactory = this._promptControlServiceFactory()
      const byTypeAbstractFactory = this._byTypeAbstractFactoryFactory(
        this._byTypeFactoryClasses,
        utils,
        new CreateDefaultStrategy(modelFactory)
      )
      const dateHelper = new DateHelper(moment)
      const domUtils = new DomUtils(window)
      const questionViewService = new QuestionViewService()
      const flowplayerService = new FlowplayerService(window)
      const flowplayerClipFactory = new FlowplayerClipFactory()
      const testExecutionFactory = new TestExecutionFactory(appContext)
      const LibEntityMappingConfigs = [TEST_EXECUTION_MAPPING_CONFIG]
      const examState = new ExamState()
      const questionCorrectnessService = new QuestionCorrectnessService()

      const environmentService = new EnvironmentService(this._environmentConfigs)

      this._entityMappingConfigs = this._entityMappingConfigs.concat(LibEntityMappingConfigs)

      return [
        ...dataServiceProviders,
        ...operationServiceProviders,
        { provide: HttpHelper, useFactory: httpHelperFactory, deps: [Http, AppContext] },
        { provide: AppContext, useValue: appContext },
        { provide: FormMapper, useValue: formMapper },
        { provide: PromptControlService, useFactory: promptControlServiceFactory, deps: [FormBuilder] },
        { provide: ModelFactory, useValue: modelFactory },
        { provide: PromptFactory, useValue: promptFactory },
        { provide: FormHelper, useValue: formHelper },
        { provide: Utils, useValue: utils },
        { provide: TreeUtils, useValue: treeUtils },
        { provide: ObjectHelper, useValue: objectHelper },
        { provide: DateHelper, useValue: dateHelper },
        { provide: TinyMceHelper, useValue: tinyMceHelper },
        { provide: MessageService, useValue: messageService },
        { provide: AppStateService, useValue: appStateService },
        { provide: EntityListRegistryService, useValue: entityListRegistryService },
        { provide: AlphabetGenerator, useValue: alphabetGenerator },
        { provide: QuestionViewFormMapper, useValue: questionViewFormMapper },
        { provide: QuestionViewService, useValue: questionViewService },
        { provide: DomUtils, useValue: domUtils },
        { provide: AppMetricsService, useValue: appMetricsService },
        { provide: RedirectHelper, useValue: redirectHelper },
        { provide: TestExecutionFactory, useValue: testExecutionFactory },
        { provide: TimerFactory, useValue: timerFactory },
        { provide: ExamState, useValue: examState },
        { provide: EnvironmentService, useValue: environmentService },
        { provide: QuestionCorrectnessService, useValue: questionCorrectnessService },
        {
          provide: MathJaxService,
          useFactory: this._mathJaxServiceFactory(window, domUtils),
          deps: [NgZone]
        },
        { provide: FlowplayerService, useValue: flowplayerService},
        { provide: FlowplayerClipFactory, useValue: flowplayerClipFactory},
        {
          provide: GuaranteedFindDecoratorFactory,
          useFactory: this._guaranteedFindDecoratorFactory(modelFactory, httpStatus),
          deps: [AppInjector]
        },
        {
          provide: AuthenticationService,
          useFactory: this._authServiceFactory(
            appContext, apiTokenFactory, ''
          ),
          deps: [HttpClient, UserService, RedirectHelper, EnvironmentService]
        },
        {
          provide: ModelRepository,
          useFactory: this._modelRepositoryFactory(
            utils,
            this._apiRoot,
            endpoints,
            this._entityMappingConfigs,
            modelFactory,
            presentersClasses,
            objectHelper
          ),
          deps: [HttpHelper, AppContext]
        },
        {
          provide: JsonApiProcessor,
          useFactory: this._operationProcessorFactory(
            utils,
            this._apiRoot,
            endpoints,
            this._entityMappingConfigs,
            modelFactory,
            uriFactory,
            objectHelper
          ),
          deps: [HttpHelper, AppContext]
        },
        {
          provide: AppInjector,
          useFactory: this._appInjectorFactory(appInjectorClasses),
          deps: [Injector]
        },
        {
          provide: EntityAbstractFactory,
          useFactory: byTypeAbstractFactory,
          deps: [AppInjector]
        },
        {
          provide: QuestionFactory,
          useFactory: this._questionFactoryFactory(modelFactory, alphabetGenerator),
          deps: [QuestionTypeService]
        },
        {
          provide: QuestionContextCreateByTypeStrategy,
          useFactory: this._questionContextCreateByTypeFactory(modelFactory),
          deps: [QuestionContextTypeService]
        },
        {
          provide: ModuleStateService,
          useFactory: this._moduleStateServiceFactory(utils),
          deps: [TestExecutionService]
        },
        { provide: AuthGuard, useClass: AuthGuard },
        {
          provide: CourseService,
          useFactory: this._courseServiceFactory(),
          deps: [HttpHelper]
        },
        {
          provide: ProductRecommendationService,
          useFactory: this._productRecommendationServiceFactory(),
          deps: [HttpHelper, CartItemAssociationService, KtpEnrollmentRecommendationService]
        },
        {
          provide: KtpEnrollmentRecommendationService,
          useFactory: this._ktpCourseRecommendationServiceFactory(dateHelper),
          deps: [GraphqlService]
        },
        {
          provide: TextQuestionAutosaver,
          useFactory: this._textQuestionAutosaverFactory(),
          deps: [UserAnswerService]
        }
      ]
    }

    private _httpHelperFactory(
      apiErrorFactory: ApiErrorFactory,
      httpStatus: HttpStatus,
      appVersion: number,
      appMetricsService: AppMetricsService
    ) {
      return (http: Http, appContext: AppContext) =>
        new HttpHelper(http, appContext, apiErrorFactory, httpStatus, appVersion, appMetricsService)
    }

    private _mathJaxServiceFactory(window: Window, domUtils: DomUtils) {
      return (ngZone: NgZone) => {
        return new MathJaxService(window, domUtils, ngZone)
      }
    }

    private _authServiceFactory(
      appContext: AppContext,
      apiTokenFactory: ApiTokenFactory,
      usersApiUrl: string
    ) {
      return (
        http: HttpClient,
        userService: UserService,
        redirectHelper: RedirectHelper,
        environmentService: EnvironmentService
      ) => {
        return new AuthenticationService(
          http,
          appContext,
          apiTokenFactory,
          userService,
          redirectHelper,
          usersApiUrl,
          environmentService
        )
      }
    }

    private _modelRepositoryFactory(
      utils: Utils,
      apiRoot: string,
      endpointMapping: {},
      entityMappingConfigs: IEntityMappingConfig[],
      modelFactory: ModelFactory,
      presenterClasses: Array<{ new(): YaysonPresenter }>,
      objectHelper: ObjectHelper
    ) {
      const yaysonStore = new Store()
      const presenterFactory = new PresenterFactory(presenterClasses, utils)
      const entityMappingFactory = new EntityMappingFactory(utils, objectHelper)
      const entityMappingManager = new EntityMappingManager(entityMappingConfigs, entityMappingFactory, utils)
      const modelIdentityMap = new ModelIdentityMap()
      const modelMapper = new ModelMapper(modelIdentityMap, modelFactory, entityMappingManager, utils)

      return(httpHelper: HttpHelper, appContext: AppContext) => {
        const jsonapiUrlGenerator = new JsonapiUrlGenerator(apiRoot, endpointMapping, entityMappingManager, appContext)
        const jsonapiBackend = new JsonApiBackend(
          httpHelper,
          appContext,
          yaysonStore,
          presenterFactory,
          jsonapiUrlGenerator,
          modelMapper
        )
        return new ModelRepository(jsonapiBackend)
      }
    }

    private _operationProcessorFactory(
      utils: Utils,
      apiRoot: string,
      endpointMapping: {},
      entityMappingConfigs: IEntityMappingConfig[],
      modelFactory: ModelFactory,
      uriFactory: UriFactory,
      objectHelper: ObjectHelper
    ) {
      const yaysonStore = new Store()
      const entityMappingFactory = new EntityMappingFactory(utils, objectHelper)
      const entityMappingManager = new EntityMappingManager(entityMappingConfigs, entityMappingFactory, utils)
      const modelIdentityMap = new ModelIdentityMap()
      const modelMapper = new ModelMapper(modelIdentityMap, modelFactory, entityMappingManager, utils)

      return (httpHelper: HttpHelper, appContext: AppContext) => {
        const jsonapiUrlGenerator = new JsonapiUrlGenerator(apiRoot, endpointMapping, entityMappingManager, appContext)
        const operationRequest = new JsonApiOperationRequest(utils, jsonapiUrlGenerator, modelMapper, uriFactory)

        return new JsonApiProcessor(httpHelper, yaysonStore, modelMapper, utils, operationRequest)
      }
    }

    private _promptControlServiceFactory() {
      return(formBuilder: FormBuilder) => {
        return new PromptControlService(formBuilder)
      }
    }

    private _appInjectorFactory(classRegistry: ClassRegistry<any>) {
      return(appInjector: Injector) => {
        return new AppInjector(appInjector, classRegistry)
      }
    }

    private _guaranteedFindDecoratorFactory(modelFactory: ModelFactory, httpStatus: HttpStatus) {
      return(appInjector: AppInjector) => {
        return new GuaranteedFindDecoratorFactory(modelFactory, appInjector, httpStatus)
      }
    }

    private _byTypeAbstractFactoryFactory(
      byTypeFactories: Array < {new(): CreateByTypeStrategy} > ,
      utils: Utils,
      createDefaultStrategy: CreateDefaultStrategy
    ) {
      return(appInjector: AppInjector) => {
        return new EntityAbstractFactory(byTypeFactories, createDefaultStrategy, appInjector, utils)
      }
    }

    private _createDataServiceProviders(): any[] {
      const defaultDataServiceClasses = [
        AnswerChoiceService,
        CategoryService,
        UserAnswerService,
        ContainerService,
        ExplanationService,
        ExplanationTypeService,
        LabelService,
        QuestionContextService,
        QuestionContextTypeService,
        QuestionPromptService,
        QuestionTypeService,
        TestExecutionService,
        TestExecutionResultService,
        TopicService,
        UserService
      ]
      const defaultProviders = this._createDefaultDataServiceProvisers(defaultDataServiceClasses)
      return [
        {
          provide: AnswerChoiceGroupService,
          useFactory: (
            modelRepository: ModelRepository,
            answerChoiceService: AnswerChoiceService,
            labelService: LabelService
          ) => new AnswerChoiceGroupService(modelRepository, answerChoiceService, labelService),
          deps: [ModelRepository, AnswerChoiceService, LabelService]
        },
        {
          provide: QuestionService,
          useFactory: (
            modelRepository: ModelRepository,
            answerChoiceGroupService: AnswerChoiceGroupService,
            explanationService: ExplanationService,
            questionPromptService: QuestionPromptService,
          ) => new QuestionService(modelRepository, answerChoiceGroupService, explanationService, questionPromptService),
          deps: [ModelRepository, AnswerChoiceGroupService, ExplanationService, QuestionPromptService]
        }
      ].concat(defaultProviders)
    }

    private _createOperationServiceProviders(): any[] {
      const defaultOperationServiceClasses = [
        TestSegmentService,
        ScoreResultService,
        UploadService
      ]
      const defaultProviders = this._createDefaultOperationServiceProviders(defaultOperationServiceClasses)
      return defaultProviders
    }

    private _createDefaultDataServiceProvisers(dataServices: any[]): any[] {
      return dataServices.map((klass) => {
        return { provide: klass, useFactory: this._defaultDataServiceFactory(klass), deps: [ModelRepository] }
      })
    }

    private _defaultDataServiceFactory(klass) {
      return(modelRepository: ModelRepository) => new klass(modelRepository)
    }

    private _createDefaultOperationServiceProviders(operationServices: any[]): any[] {
      return operationServices.map((klass) => {
        return { provide: klass, useFactory: this._defaultOperationServiceFactory(klass), deps: [JsonApiProcessor] }
      })
    }

    private _defaultOperationServiceFactory(klass) {
      return(processor: JsonApiProcessor) => new klass(processor)
    }

    private _questionFactoryFactory(modelFactory: ModelFactory, alphaberGenerator: AlphabetGenerator) {
      return(questionTypeService: QuestionTypeService) => {
        return new QuestionFactory(modelFactory, alphaberGenerator, questionTypeService)
      }
    }

    private _questionContextCreateByTypeFactory(modelFactory: ModelFactory) {
      return(questionContextTypeService: QuestionContextTypeService) => {
        return new QuestionContextCreateByTypeStrategy(questionContextTypeService, modelFactory)
      }
    }

    private _moduleStateServiceFactory(utils: Utils) {
      return (testExecutionService: TestExecutionService) => {
        return new ModuleStateService(testExecutionService, utils)
      }
    }

    private _courseServiceFactory() {
      return (httpHelper: HttpHelper) => new CourseService(httpHelper)
    }

    private _productRecommendationServiceFactory() {
      return (
          httpHelper: HttpHelper,
          cartItemAssociationService: CartItemAssociationService,
          ktpCourseRecommendationService: KtpEnrollmentRecommendationService
        ) => {
        return new ProductRecommendationService(
          httpHelper, new CourseService(httpHelper),
          cartItemAssociationService,
          ktpCourseRecommendationService
      )
      }
    }

    private _ktpCourseRecommendationServiceFactory(dateHelper: DateHelper) {
      return (graphqlService: GraphqlService) => {
        return new KtpEnrollmentRecommendationService(graphqlService, dateHelper)
      }
    }

    private _textQuestionAutosaverFactory() {
      return (userAnswerService: UserAnswerService) => {
        return new TextQuestionAutosaver(userAnswerService)
      }
    }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/providers.builder.ts